import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Tooltip,
  CircularProgress,
  IconButton,
} from '@mui/material';
import {
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useProjectsContext } from '../context/ProjectsContext';
import { useBatchUploadHandler } from '../hooks/BatchUploadHandler';
import { PageBanner } from '../../ui/PageBanner';

interface ProjectsHeaderProps {
  onRefresh?: () => unknown;
  loading?: boolean;
  projectCount?: number;
}

const ProjectsHeader: React.FC<ProjectsHeaderProps> = ({
  onRefresh,
  loading,
  projectCount = 0,
}) => {
  const { isScanning } = useProjectsContext();
  const { handleBatchFolderSelect } = useBatchUploadHandler();
  const [showCheck, setShowCheck] = useState(false);
  const prevLoading = useRef(loading);

  useEffect(() => {
    if (prevLoading.current && !loading) {
      setShowCheck(true);
      const timer = setTimeout(() => setShowCheck(false), 3000);
      return () => clearTimeout(timer);
    }
    prevLoading.current = loading;
  }, [loading]);

  return (
    <PageBanner
      variant="hero"
      data-testid="projects-header"
      sx={{
        position: 'relative',
        overflow: 'hidden',
        gridTemplateColumns: {
          xs: 'minmax(0, 1fr)',
          sm: 'minmax(0, 1fr) auto',
        },
      }}
    >
      <Box sx={{ position: 'relative', zIndex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.8 }}>
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              bgcolor: 'primary.main',
            }}
          />
          <Typography
            variant="overline"
            sx={{
              color: 'text.secondary',
              fontSize: '0.64rem',
              lineHeight: 1,
              letterSpacing: '0.16em',
              fontWeight: 700,
            }}
          >
            Studio library · {projectCount} projects
          </Typography>
        </Box>
        <Typography
          variant="h4"
          sx={{
            fontFamily: 'inherit',
            fontWeight: 600,
            fontSize: { xs: 24, md: 28 },
            letterSpacing: '-0.025em',
          }}
        >
          My Projects
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 0.25, display: { xs: 'none', sm: 'block' } }}
        >
          Your sessions, stems and version history.
        </Typography>
      </Box>

      <Box
        display="flex"
        gap={1}
        alignItems="center"
        sx={{ position: 'relative', zIndex: 1, flexShrink: 0 }}
      >
        {onRefresh && (
          <Tooltip
            title={showCheck ? 'Up to date' : 'Refresh projects'}
            placement="bottom"
          >
            <span>
              <IconButton
                onClick={() => onRefresh?.()}
                disabled={loading || isScanning}
                size="small"
                sx={{ color: showCheck ? 'success.main' : 'text.secondary' }}
              >
                {showCheck ? (
                  <CheckCircleIcon fontSize="small" />
                ) : (
                  <RefreshIcon
                    fontSize="small"
                    sx={{
                      transition: 'transform 0.6s ease',
                      ...(loading && {
                        animation: 'spin 0.8s linear infinite',
                        '@keyframes spin': {
                          from: { transform: 'rotate(0deg)' },
                          to: { transform: 'rotate(360deg)' },
                        },
                      }),
                    }}
                  />
                )}
              </IconButton>
            </span>
          </Tooltip>
        )}

        <Tooltip
          title="Select a folder containing your project files to upload"
          placement="bottom"
        >
          <Button
            variant="contained"
            startIcon={
              isScanning ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <AddIcon />
              )
            }
            onClick={handleBatchFolderSelect}
            disabled={isScanning}
            size="medium"
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              px: 1.5,
              fontSize: 13,
              whiteSpace: 'nowrap',
            }}
          >
            Upload Projects
          </Button>
        </Tooltip>
      </Box>
    </PageBanner>
  );
};

export default ProjectsHeader;
